import { MapboxUtils } from '@protector/components/map';
import type { PropertyInfoAddressPoints } from '@protector/entities/property/models';
import type { Region } from '@protector/entities/region/models';
import type { Dictionary, UUID } from '@protector/utils/types';
import type { AllGeoJSON, Polygon, Properties } from '@turf/turf';
import { bbox, ellipse, point } from '@turf/turf';
import axios from 'axios';
import { MAPBOX_KEY } from 'config/constants';
import type { RasterSource, mapboxgl } from 'core/utils/map/mapbox-gl';
import type { Feature, GeoJSON, MultiPolygon, Position } from 'geojson';
import { tryCatchWithMonitoring } from 'infra/monitoring/monitoring.utils';
import type { FitBoundsOptions } from 'mapbox-gl';
import type { MapboxStyle } from './core-map.constants';
import type { DrawPolygonFromPointProps } from './sd-mapbox.models';
import { MapboxConditionProperty, MapboxFilterOption, MapboxLayer, MapboxProperty, MapboxTile } from './sd-mapbox.models';

export const DEFAULT_POPUP_CLASSNAME = 'card-mapbox-style';
export const CURSOR_POINTER = 'pointer';

const buildRasterSource = (tile: string, scheme: 'tms' | 'xyz' | undefined = 'tms'): RasterSource => {
  return {
    type: 'raster',
    tiles: [tile],
    scheme,
    tileSize: 256
  };
};

const getTile = (tileOption: MapboxStyle) => {
  if (tileOption === MapboxTile.SATELLITE_MAPBOX) return 'mapbox/satellite-v9';
  if (tileOption === MapboxTile.LIGHT_MODE) return 'mapbox/light-v10';
  if (tileOption === MapboxTile.SATELLITE_STREETS) return 'mapbox/satellite-streets-v11';
  return 'mapbox/dark-v10';
};

const getMapboxStyleMode = async (mapboxInstance: mapboxgl.Map, tile: MapboxStyle = MapboxTile.SATELLITE_MAPBOX): Promise<void> => {
  if (!mapboxInstance) return;
  const oldStyle = mapboxInstance.getStyle();

  const url = `https://api.mapbox.com/styles/v1/${getTile(tile)}?access_token=${MAPBOX_KEY}`;

  const { data } = await axios.get<mapboxgl.Style>(url);
  // Copy current sources

  let newStyle = data;
  newStyle = { ...newStyle, sources: { ...oldStyle.sources, ...newStyle.sources } };

  // Find where to replace layers
  let labelIndex = newStyle.layers?.findIndex(el => {
    return el.id === 'country-label';
  });

  if (labelIndex === -1 && newStyle.layers) {
    labelIndex = newStyle.layers.length;
  }

  const appLayers = oldStyle.layers?.filter(el => {
    // app layers are the layers to retain, and these are any layers which have a different source set
    return 'source' in el && el.source && el.source !== 'mapbox://mapbox.satellite' && el.source !== 'mapbox' && el.source !== 'composite';
  });

  newStyle = {
    ...newStyle,
    layers: [...(newStyle.layers?.slice(0, labelIndex) ?? []), ...appLayers, ...(newStyle.layers?.slice(labelIndex, -1) ?? [])]
  };

  mapboxInstance.setStyle(newStyle);
};

const setHighlightRegion = (mapbox: mapboxgl.Map, region: Region) => {
  colorizeBordersByIds(mapbox, region.children as UUID[]);
};

const colorizeBordersByIds = (mapbox: mapboxgl.Map, regionIds: UUID[], layer: MapboxLayer = MapboxLayer.AREAS_HOVER) => {
  if (mapbox) {
    mapbox.setFilter(layer, [
      MapboxFilterOption.CASE,
      [MapboxFilterOption.IN, [MapboxConditionProperty.GET, MapboxProperty.ID], [MapboxConditionProperty.LITERAL, [...regionIds]]],
      true,
      [MapboxFilterOption.IN, [MapboxConditionProperty.GET, MapboxProperty.PARENT_ID], [MapboxConditionProperty.LITERAL, [...regionIds]]]
    ]);
  }
};

const checkTypeGeometry = (feature: Feature<Polygon | MultiPolygon>) => {
  if (feature.geometry.type === 'Polygon') {
    return feature.geometry.coordinates;
  }

  let maxArea = 0;
  let maxPolygon: Position[][] = [];

  for (let i = 0, l = feature.geometry.coordinates.length; i < l; i++) {
    const p = feature.geometry.coordinates[i];
    const area = p.length;
    if (area > maxArea) {
      maxPolygon = p;
      maxArea = area;
    }
  }

  return maxPolygon;
};

const setHighlightField = (mapbox: mapboxgl.Map, field: Region) => {
  if (mapbox) {
    mapbox.setFilter(MapboxLayer.AREAS_HOVER, [MapboxFilterOption.IN, [MapboxConditionProperty.GET, MapboxProperty.ID], field.id]);
  }
};

const drawPolygonFromPoint = ({
  lngLat,
  xSemiAxis = 100,
  ySemiAxis = 100,
  options,
  flagRemoveRevertCoordinates
}: DrawPolygonFromPointProps): Feature<Polygon | null, Properties> => {
  const points = flagRemoveRevertCoordinates ? lngLat : [lngLat[1], lngLat[0]];

  const center = point(points as unknown as GeoJSON.Position);

  return ellipse(center, xSemiAxis, ySemiAxis, options);
};

const validateReferencePoint = (
  geometry?: GeoJSON.FeatureCollection<GeoJSON.Polygon> | GeoJSON.Feature<GeoJSON.Polygon>,
  referencePointProperty?: PropertyInfoAddressPoints,
  flag?: string | boolean,
  flagRemoveRevertCoordinates?: string | boolean
): GeoJSON | undefined => {
  if (!geometry) return undefined;
  if (!flag) return geometry;

  switch (geometry.type) {
    case 'Feature':
      if (geometry.geometry?.coordinates?.length) return geometry;
      break;
    case 'FeatureCollection':
      if (geometry.features.some(feature => !!feature.geometry.coordinates?.length)) return geometry;
      break;
    default:
      break;
  }

  if (referencePointProperty?.coordinates?.length) {
    const feature = drawPolygonFromPoint({
      lngLat: referencePointProperty.coordinates,
      xSemiAxis: 1000,
      ySemiAxis: 1000,
      options: {
        units: 'meters'
      },
      flagRemoveRevertCoordinates
    });

    return {
      type: 'FeatureCollection',
      features: [feature]
    } as GeoJSON.FeatureCollection;
  }

  return undefined;
};

const getPaddingWidthFromFitBoundsOptions = (options?: FitBoundsOptions) => {
  let paddingWidth = 0;

  if (typeof options?.padding === 'number' && !Number.isNaN(options.padding)) {
    paddingWidth = options.padding * 2;
  }

  if (typeof options?.padding === 'object') {
    paddingWidth = options.padding.left + options.padding.right;
  }

  return paddingWidth;
};

const getMapboxContainerWidth = (instance: mapboxgl.Map) => {
  return instance.getContainer().getBoundingClientRect().width;
};

const isValidCoordinate = (coord: number) => !!coord && !Number.isNaN(coord) && coord !== null && coord !== undefined;

const safeFitBounds = (
  instance: mapboxgl.Map,
  geometry: AllGeoJSON,
  referencePoint?: PropertyInfoAddressPoints,
  options?: FitBoundsOptions,
  flagRemoveRevertCoordinates?: boolean
) => {
  void tryCatchWithMonitoring(
    () => {
      const adjustedGeometry = validateReferencePoint(
        geometry as Feature<Polygon>,
        referencePoint,
        true,
        flagRemoveRevertCoordinates
      ) as AllGeoJSON;

      if (!adjustedGeometry) return;

      const paddingWidth = getPaddingWidthFromFitBoundsOptions(options);
      const mapWidth = getMapboxContainerWidth(instance);

      if (!mapWidth) return;

      const bounds = bbox(adjustedGeometry) as [number, number, number, number];

      if (bounds.every(isValidCoordinate)) {
        instance.fitBounds(bounds, {
          ...options,
          padding: mapWidth >= paddingWidth ? options?.padding : 0
        });
      }
    },
    { errorType: 'MapboxError', domain: 'MapFeature' }
  );
};

const getBackgroundTile = (systemFlags: Dictionary<string | boolean> | null) => {
  if (systemFlags?.P40_48231_map_with_locations) {
    return MapboxTile.SATELLITE_STREETS;
  }

  return MapboxTile.SATELLITE_MAPBOX;
};

const MapboxUtil = {
  checkTypeGeometry,
  buildRasterSource,
  getMapboxStyleMode,
  setHighlightField,
  setHighlightRegion,
  colorizeBordersByIds,
  drawPolygonFromPoint,
  validateReferencePoint,
  safeFitBounds,
  getBackgroundTile,
  removeLayerIfExists: MapboxUtils.removeLayerIfExists,
  removeLayersIfExists: MapboxUtils.removeLayersIfExists,
  removeSourceIfExists: MapboxUtils.removeSourceIfExists,
  removeSourcesIfExists: MapboxUtils.removeSourcesIfExists,
  removeImageIfExists: MapboxUtils.removeImageIfExists,
  removeImagesIfExists: MapboxUtils.removeImagesIfExists
};

export default MapboxUtil;
